import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Cable, Download, Loader2, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { KABELBAUM_MASTER } from "@/data/kabelbaumMaster";

/* ---------- Helpers ---------- */
const fmt = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtEuro = (n) => `${fmt.format(Number.isFinite(+n) ? +n : 0)} €`;
const num = (v) => (Number.isFinite(+v) ? +v : 0);

const kabelEk = (row) => num(row.laenge_m) * num(row.menge) * num(row.ek_meterpreis);
const kabelVk = (row) => num(row.laenge_m) * num(row.menge) * num(row.vk_meterpreis);
const kompEk = (row) => num(row.menge) * num(row.ek_stueckpreis) * num(row.steuer);
const kompVk = (row) => num(row.menge) * num(row.vk_stueckpreis) * num(row.steuer);

function computeSectionTotals(section) {
  const kabel_ek = (section.kabel || []).reduce((s, r) => s + kabelEk(r), 0);
  const kabel_vk = (section.kabel || []).reduce((s, r) => s + kabelVk(r), 0);
  const komp_ek = (section.komponenten || []).reduce((s, r) => s + kompEk(r), 0);
  const komp_vk = (section.komponenten || []).reduce((s, r) => s + kompVk(r), 0);
  const arb_ek = num(section.arbeitszeit_ek);
  const arb_vk = num(section.arbeitszeit_vk);
  return {
    kabel_ek, kabel_vk, komp_ek, komp_vk, arb_ek, arb_vk,
    total_ek: kabel_ek + komp_ek + arb_ek,
    total_vk: kabel_vk + komp_vk + arb_vk,
    marge: (kabel_vk + komp_vk + arb_vk) - (kabel_ek + komp_ek + arb_ek),
  };
}

function computeGrandTotals(sektionen) {
  let kabel_ek = 0, kabel_vk = 0, komp_ek = 0, komp_vk = 0, arb_ek = 0, arb_vk = 0;
  for (const s of sektionen) {
    const t = computeSectionTotals(s);
    kabel_ek += t.kabel_ek; kabel_vk += t.kabel_vk;
    komp_ek += t.komp_ek; komp_vk += t.komp_vk;
    arb_ek += t.arb_ek; arb_vk += t.arb_vk;
  }
  const total_ek = kabel_ek + komp_ek + arb_ek;
  const total_vk = kabel_vk + komp_vk + arb_vk;
  const marge = total_vk - total_ek;
  const marge_pct = total_vk > 0 ? (marge / total_vk) * 100 : 0;
  return { kabel_ek, kabel_vk, komp_ek, komp_vk, arb_ek, arb_vk, total_ek, total_vk, marge, marge_pct };
}

/* ---------- Editable Cell ---------- */
const CellInput = ({ value, onChange, type = "text", step, testId, className = "", align = "left" }) => {
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return (
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      type={type}
      step={step}
      className={`w-full bg-transparent border-0 outline-none focus:bg-amber-50 focus:ring-1 focus:ring-amber-400 rounded px-1 py-0.5 text-sm ${alignCls} ${className}`}
      data-testid={testId}
    />
  );
};

/* ---------- Page ---------- */
export default function KabelbaumPage() {
  const [projekt, setProjekt] = useState("Kabelbaum System");
  const [sektionen, setSektionen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const saveTimeoutRef = useRef(null);
  const suppressAutosaveRef = useRef(true);

  /* Load state on mount */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/api/kabelbaum");
        if (cancelled) return;
        setProjekt(res.data.projekt || "Kabelbaum System");
        setSektionen(res.data.sektionen || []);
        setActiveTab((res.data.sektionen?.[0]?.sektion) || "");
      } catch (e) {
        if (e.response?.status === 404) {
          // Seed with master
          setProjekt(KABELBAUM_MASTER.projekt);
          setSektionen(KABELBAUM_MASTER.sektionen);
          setActiveTab(KABELBAUM_MASTER.sektionen[0]?.sektion || "");
        } else {
          toast.error("Kabelbaum konnte nicht geladen werden");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          // Allow autosave after next tick
          setTimeout(() => { suppressAutosaveRef.current = false; }, 200);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* Auto-save on change (debounced 800ms) */
  const saveNow = useCallback(async (data) => {
    setSaving(true);
    try {
      await api.put("/api/kabelbaum", { projekt: projekt, sektionen: data });
      setDirty(false);
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }, [projekt]);

  useEffect(() => {
    if (suppressAutosaveRef.current || loading) return;
    setDirty(true);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveNow(sektionen), 800);
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [sektionen, saveNow, loading]);

  const grand = useMemo(() => computeGrandTotals(sektionen), [sektionen]);

  /* Mutators */
  const updateSection = (idx, patch) => {
    setSektionen((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const updateKabelRow = (sIdx, rIdx, patch) => {
    setSektionen((prev) => prev.map((s, i) => {
      if (i !== sIdx) return s;
      const kabel = (s.kabel || []).map((r, k) => (k === rIdx ? { ...r, ...patch } : r));
      return { ...s, kabel };
    }));
  };
  const updateKompRow = (sIdx, rIdx, patch) => {
    setSektionen((prev) => prev.map((s, i) => {
      if (i !== sIdx) return s;
      const komponenten = (s.komponenten || []).map((r, k) => (k === rIdx ? { ...r, ...patch } : r));
      return { ...s, komponenten };
    }));
  };
  const addKabelRow = (sIdx) => {
    setSektionen((prev) => prev.map((s, i) => (
      i !== sIdx ? s : { ...s, kabel: [...(s.kabel || []), {
        beschreibung: "Neues Kabel", markierung: "", querschnitt: "1,5mm²",
        laenge_m: 0, menge: 1, ende_a: "—", ende_b: "—", ek_meterpreis: 0, vk_meterpreis: 0,
      }] }
    )));
  };
  const addKompRow = (sIdx) => {
    setSektionen((prev) => prev.map((s, i) => (
      i !== sIdx ? s : { ...s, komponenten: [...(s.komponenten || []), {
        beschreibung: "Neue Komponente", menge: 1, ek_stueckpreis: 0, vk_stueckpreis: 0, steuer: 1.19,
      }] }
    )));
  };
  const removeKabelRow = (sIdx, rIdx) => {
    setSektionen((prev) => prev.map((s, i) => (
      i !== sIdx ? s : { ...s, kabel: (s.kabel || []).filter((_, k) => k !== rIdx) }
    )));
  };
  const removeKompRow = (sIdx, rIdx) => {
    setSektionen((prev) => prev.map((s, i) => (
      i !== sIdx ? s : { ...s, komponenten: (s.komponenten || []).filter((_, k) => k !== rIdx) }
    )));
  };

  const resetToMaster = async () => {
    try {
      await api.delete("/api/kabelbaum");
      suppressAutosaveRef.current = true;
      setProjekt(KABELBAUM_MASTER.projekt);
      setSektionen(KABELBAUM_MASTER.sektionen);
      setActiveTab(KABELBAUM_MASTER.sektionen[0]?.sektion || "");
      setDirty(false);
      toast.success("Kabelbaum auf Master-Daten zurückgesetzt");
      setTimeout(() => { suppressAutosaveRef.current = false; }, 200);
    } catch {
      toast.error("Zurücksetzen fehlgeschlagen");
    }
  };

  const exportSectionPdf = (section) => generateSectionPdf(section, projekt);
  const exportAllPdf = () => generateAllSectionsPdf(sektionen, projekt);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Kabelbaum wird geladen…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="kabelbaum-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Cable className="w-7 h-7" /> Produktion / Kabelbäume</h1>
          <p className="text-sm text-slate-500">{projekt} · Live-Kalkulation für Lexware-Übernahme</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 flex items-center gap-1" data-testid="kabelbaum-save-status">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {saving ? "Speichere…" : dirty ? "Nicht gespeichert" : "Gespeichert"}
          </span>
          <Button variant="outline" size="sm" onClick={exportAllPdf} data-testid="kabelbaum-export-all-pdf">
            <Download className="w-4 h-4 mr-1" /> Alle Sektionen PDF
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="kabelbaum-reset-btn"><RotateCcw className="w-4 h-4 mr-1" /> Reset</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Kabelbaum zurücksetzen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Alle deine Änderungen werden verworfen und die Master-Daten geladen. Diese Aktion kann nicht rückgängig gemacht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={resetToMaster}>Zurücksetzen</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Summary card */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 rounded-xl border bg-white shadow-sm" data-testid="kabelbaum-summary">
        <SummaryItem label="Kabel EK" value={fmtEuro(grand.kabel_ek)} />
        <SummaryItem label="Kabel VK" value={fmtEuro(grand.kabel_vk)} />
        <SummaryItem label="Komponenten EK" value={fmtEuro(grand.komp_ek)} />
        <SummaryItem label="Komponenten VK" value={fmtEuro(grand.komp_vk)} />
        <SummaryItem label="Arbeitszeit EK" value={fmtEuro(grand.arb_ek)} />
        <SummaryItem label="Arbeitszeit VK" value={fmtEuro(grand.arb_vk)} />
        <SummaryItem label="Total EK" value={fmtEuro(grand.total_ek)} strong />
        <SummaryItem
          label="Marge"
          value={`${fmtEuro(grand.marge)} (${fmt.format(grand.marge_pct)} %)`}
          strong
          tone={grand.marge >= 0 ? "positive" : "negative"}
        />
      </div>

      {/* Big VK total */}
      <div className="rounded-2xl border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-white shadow-lg p-6" data-testid="kabelbaum-total-vk">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Verkaufspreis Gesamt</div>
            <div className="text-5xl font-extrabold text-emerald-900 tabular-nums" data-testid="kabelbaum-total-vk-value">
              {fmtEuro(grand.total_vk)}
            </div>
            <div className="text-xs text-slate-500 mt-1">(für Lexware-Übernahme — aktualisiert sich live bei jeder Änderung)</div>
          </div>
          <div className="text-right text-xs text-slate-600">
            Total EK: <strong>{fmtEuro(grand.total_ek)}</strong><br />
            Marge: <strong className={grand.marge >= 0 ? "text-emerald-700" : "text-rose-700"}>{fmtEuro(grand.marge)} ({fmt.format(grand.marge_pct)} %)</strong>
          </div>
        </div>
      </div>

      {/* Section tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1" data-testid="kabelbaum-tabs">
          {sektionen.map((s, i) => {
            const t = computeSectionTotals(s);
            return (
              <TabsTrigger key={s.sektion + i} value={s.sektion} className="flex flex-col items-start gap-0.5 py-2 px-3" data-testid={`kabelbaum-tab-${i}`}>
                <span className="font-semibold text-sm">{s.sektion}</span>
                <span className="text-[10px] text-slate-500">VK {fmtEuro(t.total_vk)}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {sektionen.map((s, sIdx) => {
          const t = computeSectionTotals(s);
          return (
            <TabsContent key={s.sektion + sIdx} value={s.sektion} className="space-y-4">
              <SectionHeader
                section={s}
                totals={t}
                onArbeitEkChange={(v) => updateSection(sIdx, { arbeitszeit_ek: num(v) })}
                onArbeitVkChange={(v) => updateSection(sIdx, { arbeitszeit_vk: num(v) })}
                onPdf={() => exportSectionPdf(s)}
                sIdx={sIdx}
              />
              <KabelTable
                sIdx={sIdx}
                rows={s.kabel || []}
                onUpdate={(rIdx, patch) => updateKabelRow(sIdx, rIdx, patch)}
                onAdd={() => addKabelRow(sIdx)}
                onRemove={(rIdx) => removeKabelRow(sIdx, rIdx)}
              />
              <KomponentenTable
                sIdx={sIdx}
                rows={s.komponenten || []}
                onUpdate={(rIdx, patch) => updateKompRow(sIdx, rIdx, patch)}
                onAdd={() => addKompRow(sIdx)}
                onRemove={(rIdx) => removeKompRow(sIdx, rIdx)}
              />
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

/* ---------- Section header ---------- */
function SectionHeader({ section, totals, onArbeitEkChange, onArbeitVkChange, onPdf, sIdx }) {
  return (
    <div className="rounded-lg border bg-slate-50 p-4">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h2 className="text-xl font-bold" data-testid={`kabelbaum-section-title-${sIdx}`}>{section.sektion}</h2>
        <Button variant="outline" size="sm" onClick={onPdf} data-testid={`kabelbaum-section-pdf-${sIdx}`}>
          <Download className="w-4 h-4 mr-1" /> Produktionsblatt PDF
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <MiniStat label="Kabel EK / VK" value={`${fmtEuro(totals.kabel_ek)} / ${fmtEuro(totals.kabel_vk)}`} />
        <MiniStat label="Komponenten EK / VK" value={`${fmtEuro(totals.komp_ek)} / ${fmtEuro(totals.komp_vk)}`} />
        <div className="flex flex-col">
          <span className="text-xs text-slate-500">Arbeitszeit EK</span>
          <CellInput
            value={section.arbeitszeit_ek ?? 0}
            onChange={onArbeitEkChange}
            type="number"
            step="0.01"
            testId={`kabelbaum-section-${sIdx}-arb-ek`}
            className="bg-white border rounded"
          />
        </div>
        <div className="flex flex-col">
          <span className="text-xs text-slate-500">Arbeitszeit VK</span>
          <CellInput
            value={section.arbeitszeit_vk ?? 0}
            onChange={onArbeitVkChange}
            type="number"
            step="0.01"
            testId={`kabelbaum-section-${sIdx}-arb-vk`}
            className="bg-white border rounded"
          />
        </div>
        <MiniStat label="Sektions-VK" value={fmtEuro(totals.total_vk)} strong tone="positive" />
      </div>
    </div>
  );
}

/* ---------- Kabel table ---------- */
function KabelTable({ sIdx, rows, onUpdate, onAdd, onRemove }) {
  return (
    <div className="rounded-lg border bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-b">
        <h3 className="font-semibold">Kabel ({rows.length})</h3>
        <Button variant="outline" size="sm" onClick={onAdd} data-testid={`kabelbaum-add-kabel-${sIdx}`}>
          <Plus className="w-4 h-4 mr-1" /> Kabel hinzufügen
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="p-2 text-left min-w-[180px]">Beschreibung</th>
              <th className="p-2 text-left">Markierung</th>
              <th className="p-2 text-left">Querschn.</th>
              <th className="p-2 text-right">Länge (m)</th>
              <th className="p-2 text-right">Menge</th>
              <th className="p-2 text-left min-w-[110px]">Ende A</th>
              <th className="p-2 text-left min-w-[110px]">Ende B</th>
              <th className="p-2 text-right">EK/m</th>
              <th className="p-2 text-right">VK/m</th>
              <th className="p-2 text-right">EK Gesamt</th>
              <th className="p-2 text-right">VK Gesamt</th>
              <th className="p-2 text-right">Marge</th>
              <th className="p-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, rIdx) => {
              const ek = kabelEk(r), vk = kabelVk(r), marge = vk - ek;
              return (
                <tr key={rIdx} className="border-t hover:bg-slate-50" data-testid={`kabelbaum-kabel-row-${sIdx}-${rIdx}`}>
                  <td className="p-1"><CellInput value={r.beschreibung} onChange={(v) => onUpdate(rIdx, { beschreibung: v })} /></td>
                  <td className="p-1"><CellInput value={r.markierung} onChange={(v) => onUpdate(rIdx, { markierung: v })} /></td>
                  <td className="p-1"><CellInput value={r.querschnitt} onChange={(v) => onUpdate(rIdx, { querschnitt: v })} /></td>
                  <td className="p-1"><CellInput value={r.laenge_m} onChange={(v) => onUpdate(rIdx, { laenge_m: v })} type="number" step="0.01" align="right" /></td>
                  <td className="p-1"><CellInput value={r.menge} onChange={(v) => onUpdate(rIdx, { menge: v })} type="number" step="1" align="right" /></td>
                  <td className="p-1"><CellInput value={r.ende_a} onChange={(v) => onUpdate(rIdx, { ende_a: v })} /></td>
                  <td className="p-1"><CellInput value={r.ende_b} onChange={(v) => onUpdate(rIdx, { ende_b: v })} /></td>
                  <td className="p-1"><CellInput value={r.ek_meterpreis} onChange={(v) => onUpdate(rIdx, { ek_meterpreis: v })} type="number" step="0.001" align="right" /></td>
                  <td className="p-1"><CellInput value={r.vk_meterpreis} onChange={(v) => onUpdate(rIdx, { vk_meterpreis: v })} type="number" step="0.001" align="right" /></td>
                  <td className="p-2 text-right tabular-nums">{fmtEuro(ek)}</td>
                  <td className="p-2 text-right tabular-nums">{fmtEuro(vk)}</td>
                  <td className={`p-2 text-right tabular-nums ${marge < 0 ? "text-rose-600" : ""}`}>{fmtEuro(marge)}</td>
                  <td className="p-1 text-center">
                    <button className="text-slate-400 hover:text-rose-600" onClick={() => onRemove(rIdx)} data-testid={`kabelbaum-remove-kabel-${sIdx}-${rIdx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan="13" className="p-4 text-center text-slate-400">Noch keine Kabel — klicke „Kabel hinzufügen"</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Komponenten table ---------- */
function KomponentenTable({ sIdx, rows, onUpdate, onAdd, onRemove }) {
  return (
    <div className="rounded-lg border bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-b">
        <h3 className="font-semibold">Komponenten ({rows.length})</h3>
        <Button variant="outline" size="sm" onClick={onAdd} data-testid={`kabelbaum-add-komp-${sIdx}`}>
          <Plus className="w-4 h-4 mr-1" /> Komponente hinzufügen
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="p-2 text-left min-w-[200px]">Beschreibung</th>
              <th className="p-2 text-right">Menge</th>
              <th className="p-2 text-right">EK Stück</th>
              <th className="p-2 text-right">VK Stück</th>
              <th className="p-2 text-right">Steuer</th>
              <th className="p-2 text-right">EK Gesamt</th>
              <th className="p-2 text-right">VK Gesamt</th>
              <th className="p-2 text-right">Marge</th>
              <th className="p-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, rIdx) => {
              const ek = kompEk(r), vk = kompVk(r), marge = vk - ek;
              return (
                <tr key={rIdx} className="border-t hover:bg-slate-50" data-testid={`kabelbaum-komp-row-${sIdx}-${rIdx}`}>
                  <td className="p-1"><CellInput value={r.beschreibung} onChange={(v) => onUpdate(rIdx, { beschreibung: v })} /></td>
                  <td className="p-1"><CellInput value={r.menge} onChange={(v) => onUpdate(rIdx, { menge: v })} type="number" step="1" align="right" /></td>
                  <td className="p-1"><CellInput value={r.ek_stueckpreis} onChange={(v) => onUpdate(rIdx, { ek_stueckpreis: v })} type="number" step="0.001" align="right" /></td>
                  <td className="p-1"><CellInput value={r.vk_stueckpreis} onChange={(v) => onUpdate(rIdx, { vk_stueckpreis: v })} type="number" step="0.001" align="right" /></td>
                  <td className="p-1"><CellInput value={r.steuer} onChange={(v) => onUpdate(rIdx, { steuer: v })} type="number" step="0.01" align="right" /></td>
                  <td className="p-2 text-right tabular-nums">{fmtEuro(ek)}</td>
                  <td className="p-2 text-right tabular-nums">{fmtEuro(vk)}</td>
                  <td className={`p-2 text-right tabular-nums ${marge < 0 ? "text-rose-600" : ""}`}>{fmtEuro(marge)}</td>
                  <td className="p-1 text-center">
                    <button className="text-slate-400 hover:text-rose-600" onClick={() => onRemove(rIdx)} data-testid={`kabelbaum-remove-komp-${sIdx}-${rIdx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan="9" className="p-4 text-center text-slate-400">Noch keine Komponenten — klicke „Komponente hinzufügen"</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Small display components ---------- */
function SummaryItem({ label, value, strong = false, tone = "" }) {
  const toneCls = tone === "positive" ? "text-emerald-700" : tone === "negative" ? "text-rose-700" : "text-slate-800";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`tabular-nums ${strong ? "text-lg font-bold" : "text-sm"} ${toneCls}`}>{value}</div>
    </div>
  );
}
function MiniStat({ label, value, strong = false, tone = "" }) {
  const toneCls = tone === "positive" ? "text-emerald-700" : tone === "negative" ? "text-rose-700" : "";
  return (
    <div className="flex flex-col">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`tabular-nums ${strong ? "text-base font-bold" : "text-sm"} ${toneCls}`}>{value}</span>
    </div>
  );
}

/* ---------- PDF generation ---------- */
function _pdfSection(doc, section, isFirst) {
  if (!isFirst) doc.addPage();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(`Produktionsblatt: ${section.sektion}`, 14, 15);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text(`Generiert: ${new Date().toLocaleDateString("de-DE")}`, 14, 21);

  const t = computeSectionTotals(section);
  doc.setFontSize(9);
  doc.text(
    `Kabel EK/VK: ${fmtEuro(t.kabel_ek)} / ${fmtEuro(t.kabel_vk)}   ·   Komponenten EK/VK: ${fmtEuro(t.komp_ek)} / ${fmtEuro(t.komp_vk)}   ·   Arbeitszeit: ${fmtEuro(t.arb_ek)} / ${fmtEuro(t.arb_vk)}   ·   Total VK: ${fmtEuro(t.total_vk)}`,
    14, 27, { maxWidth: 180 }
  );

  let y = 34;
  if ((section.kabel || []).length > 0) {
    autoTable(doc, {
      startY: y,
      head: [["Markierung", "Beschreibung", "Querschn.", "Länge m", "Menge", "Ende A", "Ende B"]],
      body: section.kabel.map((r) => [
        r.markierung || "—",
        r.beschreibung,
        r.querschnitt,
        String(num(r.laenge_m)).replace(".", ","),
        num(r.menge),
        r.ende_a || "—",
        r.ende_b || "—",
      ]),
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  if ((section.komponenten || []).length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Komponenten", 14, y);
    y += 2;
    autoTable(doc, {
      startY: y + 2,
      head: [["Beschreibung", "Menge"]],
      body: section.komponenten.map((r) => [r.beschreibung, num(r.menge)]),
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [39, 174, 96], textColor: 255 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Notizen mit linierten Zeilen
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y > pageHeight - 60) { doc.addPage(); y = 20; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Notizen", 14, y);
  y += 5;
  doc.setDrawColor(160);
  for (let i = 0; i < 12; i++) {
    if (y > pageHeight - 15) break;
    doc.line(14, y, 196, y);
    y += 8;
  }
}

function generateSectionPdf(section, projekt) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  _pdfSection(doc, section, true);
  doc.save(`Kabelbaum_${section.sektion.replace(/[^\w]+/g, "_")}.pdf`);
}

function generateAllSectionsPdf(sektionen, projekt) {
  if (!sektionen || sektionen.length === 0) return;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  sektionen.forEach((s, i) => _pdfSection(doc, s, i === 0));
  doc.save(`Kabelbaum_${(projekt || "System").replace(/[^\w]+/g, "_")}_Alle.pdf`);
}
